<template>
    <EditComponent
        dialog-id="editRecommendationsDialog"
        open-dialog-event="recommendations:edit-mode"
        updated-event="recommendations:updated"
        :emit-close-function="emitCloseEvent"
        :confirm-function="handleConfirm"
        ignore-default-success-message
        confirm-data-testid="reco-request-send"
    >
        <template #edit-header>
            <p>{{ t('profile.section.header.recommendations.ask.label') }}</p>
        </template>
        <template #edit-body>
            <VJoyWrapper>
                <VJoyHighlight level="info" :display-icon="true" class="joy-mb-4">
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <p v-html="highlightContent"></p>
                </VJoyHighlight>
                <div>
                    <form :id="formId" data-testid="profile-recommendation-form">
                        <VJoyWrapper item-stretch>
                            <EmailsInput v-model="r$.$value.emails" :field="r$.emails" />
                            <ExperienceSelector v-if="experiences?.items.length" v-model="r$.$value.experienceId" :experiences="experiences.items" />
                            <MessageInput v-model="r$.$value.message" :field="r$.message" :original-message="originalMessage" />
                        </VJoyWrapper>
                    </form>
                </div>
            </VJoyWrapper>
        </template>
    </EditComponent>
</template>

<script setup lang="ts">
    import {onMounted, onUnmounted} from 'vue';
    import {createRule, type Maybe} from '@regle/core';
    import {required, isFilled} from '@regle/rules';
    import {storeToRefs} from 'pinia';
    import {reactive, ref, useTranslation} from '#imports';
    import {useRegle, validateForm} from '@malt/forms';
    import {pushVJoySnackbar, VJoyHighlight, VJoyWrapper} from '@maltjoy/core-vue';
    import {useProfileTracking} from '~/composables/useProfileTracking';
    import type {RecommendationRequestStartedEntryPoint} from '~/config/TrackingEvents';
    import EmailsInput from './ui/EmailsInput.vue';
    import ExperienceSelector from './ui/ExperienceSelector.vue';
    import MessageInput from './ui/MessageInput.vue';
    import EditComponent from '@/components/edit/EditComponent.vue';
    import {eventBus} from '@/config/events';
    import type {InvitationsRequest} from '@/store/profileStore';
    import {useProfileStore} from '@/store/profileStore';

    const {t} = useTranslation();
    const {sendRecommendationRequest} = useProfileStore();
    const {experiences} = storeToRefs(useProfileStore());
    const {profile} = storeToRefs(useProfileStore());

    const formId = 'profile-recommendation-form';

    const originalMessage = t('recommendation.defaultAskMessage');

    const defaultState: InvitationsRequest = {
        experienceId: null,
        message: originalMessage,
        emails: '',
    };

    const state = reactive<InvitationsRequest>({...defaultState});

    const emailListPattern = /^(([a-zA-Z0-9_\-.+]+)@([a-zA-Z0-9_\-.+]+)\.([a-zA-Z]{2,5}){1,25}(($)|( *,+ *$)|( *,+ *(?=[a-zA-Z0-9_\-.]))))*$/;

    const emailsValidation = createRule({
        validator: (value: Maybe<string>) => {
            if (isFilled(value)) {
                return emailListPattern.test(value);
            }
            return true;
        },
        message: () => t('recommendation.saveerror.invitation.emailsinvalid'),
    });

    const {r$} = useRegle(state, {
        experienceId: {},
        message: {
            required,
        },
        emails: {
            required,
            emailsValidation,
        },
    });

    const highlightContent = t('recommendations.ask.legend');

    function emitCloseEvent(): void {
        resetValues();
    }

    function resetValues(): void {
        r$.$reset({toState: {...defaultState}});
        entryPoint.value = undefined;
    }

    const {trackAskProfileRecommendationSent} = useProfileTracking();
    async function handleConfirm() {
        const {valid, data} = await validateForm(r$, {formId});
        if (!valid) {
            return false;
        }

        try {
            const experienceId = data.experienceId?.length ? data.experienceId : null;
            await sendRecommendationRequest({
                ...data,
                experienceId,
            });
            pushVJoySnackbar({
                props: {
                    message: t('profile.recommendations.ask.sent', {emails: data.emails, count: data.emails.split(',').length}),
                },
            });
            trackAskProfileRecommendationSent(profile.value!.profileId, entryPoint.value, data.emails.split(',').length, experienceId);
            resetValues();
            eventBus.emit('recommendations:updated');
        } catch (error: unknown) {
            if (error && typeof error === 'object' && 'status' in error && error.status === 403) {
                pushVJoySnackbar({
                    props: {
                        message: t('profile.recommendations.send.error.message.too.many'),
                        level: 'error',
                    },
                });
            } else {
                pushVJoySnackbar({
                    props: {
                        message: t('generic.error.unexpected'),
                        level: 'error',
                    },
                });
            }
        }
    }

    const {trackRecommendationRequestStarted} = useProfileTracking();
    const entryPoint = ref<RecommendationRequestStartedEntryPoint>();
    function onRecommendationsOpened(e: {entryPoint: RecommendationRequestStartedEntryPoint}) {
        entryPoint.value = e.entryPoint;
        trackRecommendationRequestStarted(profile.value!.profileId, e.entryPoint);
    }

    onMounted(() => {
        eventBus.on('recommendations:edit-mode', onRecommendationsOpened);
    });

    onUnmounted(() => {
        eventBus.off('recommendations:edit-mode', onRecommendationsOpened);
    });
</script>
<style scoped lang="scss">
    .recommendation-list {
        &__item {
            width: 100%;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
        }
    }
</style>
